import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsContext = createContext({
  staticProducts: [],
  dynamicProducts: [],
  allProducts: [],
  loading: false,
  refresh: () => {},
});

/**
 * Products are 100% DB-driven. The public storefront reads the same
 * `GET /api/products` collection that Admin › Catalogue writes to.
 * No static merge, no seed fallback, no hidden hardcoded arrays.
 */
export const ProductsProvider = ({ children }) => {
  const [dynamic, setDynamic] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`);
      setDynamic(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Products API unavailable — empty catalogue.");
      setDynamic([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refetch when returning to the tab (admin edits → live site without hard refresh).
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Public storefront rules — hide products the admin marked hidden / draft / archived.
  const publishedProducts = dynamic.filter((p) => {
    const s = (p.status || "available").toLowerCase();
    if (s === "hidden" || s === "archived" || s === "draft" || s === "unpublished") return false;
    if (p.published === false) return false;
    return true;
  });

  return (
    <ProductsContext.Provider
      value={{
        staticProducts: [],
        dynamicProducts: dynamic,
        allProducts: publishedProducts,
        loading,
        refresh,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => useContext(ProductsContext);

/** Look up any product by slug. Returns null if the admin hasn't published it. */
export const useProductBySlug = (slug) => {
  const { allProducts } = useProducts();
  return allProducts.find((p) => p.slug === slug) || null;
};
